import { Edit, useForm, useSelect } from "@refinedev/antd";
import { Form, Input, Select, Tag, Space, Row, Col } from "antd";
import { IResourceComponentsProps } from "@refinedev/core";
import {
  IdcardOutlined,
  MailOutlined,
  SettingOutlined,
  SafetyOutlined,
} from "@ant-design/icons";

import { ILicence, IVessel } from "interfaces";
import React from "react";
import { FormSection, FormFieldGrid } from "components/form";

export const VesselEdit: React.FC<IResourceComponentsProps> = () => {
  const {
    formProps,
    saveButtonProps,
    query,
    formLoading: isLoading,
  } = useForm<IVessel>();

  function LicenceSelectComponent() {
    const { selectProps } = useSelect<ILicence>({
      resource: "licence",
      optionLabel: "ukhoLicenceId",
      optionValue: "id",

      filters: query?.data?.data?.licence?.id
        ? [
            {
              field: "id",
              operator: "eq",
              value: query?.data?.data?.licence?.id,
            },
          ]
        : [],

      pagination: {
        mode: "server"
      }
    });
    return (
      <Form.Item
        label="UKHO Licence"
        name={["licence", "id"]}
        rules={[{ required: true }]}
        initialValue={query?.data?.data?.licence?.ukhoLicenceId}
      >
        <Select {...selectProps} disabled />
      </Form.Item>
    );
  }

  return (
    <Edit
      breadcrumb={false}
      saveButtonProps={saveButtonProps}
      headerProps={{
        onBack: () => window.history.back(),
        subTitle: "edit vessel",
        tags: isLoading ? (
          <Tag color="orange">loading</Tag>
        ) : (
          <Tag color="green">live</Tag>
        ),
      }}
    >
      <Form {...formProps} layout="vertical">
        <Space direction="vertical" size={24} style={{ width: "100%" }}>
          <FormSection
            title="Vessel Identity"
            subtitle="Core identification details"
            icon={<IdcardOutlined />}
            variant="elevated"
          >
            <FormFieldGrid columns={2}>
              <Form.Item
                label="Vessel Name"
                name="name"
                rules={[{ required: true }]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                label="Vessel Type"
                name="type"
                rules={[{ required: true }]}
              >
                <Select
                  options={[
                    { label: "Dry Cargo", value: "Dry Cargo" },
                    { label: "Oil Tanker", value: "Oil Tanker" },
                    { label: "Chemical Tanker", value: "Chemical Tanker" },
                    { label: "Oil / Chemical Tanker", value: "Oil / Chemical Tanker" },
                    { label: "Bulk Carrier", value: "Bulk Carrier" },
                    { label: "General Cargo Vessel", value: "General Cargo Vessel" },
                    { label: "Container Vessel", value: "Container Vessel" },
                    { label: "Crude Carrier", value: "Crude Carrier" },
                    { label: "Liquefied Gas Carrier", value: "Liquefied Gas Carrier" },
                    { label: "Specialised Cargoes", value: "Specialised Cargoes" },
                    { label: "Passenger Vessels", value: "Passenger Vessels" },
                    { label: "Livestock Carriers", value: "Livestock Carriers" },
                    { label: "Heavy-lift / Project Cargo", value: "Heavy-lift / Project Cargo" },
                    { label: "Vessels", value: "Vessels" },
                    { label: "Tugs", value: "Tugs" },
                    { label: "Pipe-Layer", value: "Pipe-Layer" },
                    { label: "Reefer Vessels", value: "Reefer Vessels" },
                    { label: "Ro-Ro Vessels", value: "Ro-Ro Vessels" },
                    { label: "Offshore Vessels", value: "Offshore Vessels" },
                    { label: "Non IMO Vessels", value: "Non IMO Vessels" },
                  ]}
                />
              </Form.Item>
              <Form.Item
                label="IMO Number"
                name="imoNumber"
                rules={[{ required: true }]}
              >
                <Input disabled />
              </Form.Item>
              <Form.Item label="Call Sign" name="callSign">
                <Input />
              </Form.Item>
            </FormFieldGrid>
            <Row gutter={[24, 16]}>
              <Col xs={24} md={12}>
                {query?.isSuccess ? (
                  <LicenceSelectComponent />
                ) : null}
              </Col>
              <Col xs={24} md={12}>
                <Form.Item label="Hold Status" name="holdStatus">
                  <Select
                    options={[
                      { label: "On Hold", value: "on hold" },
                      { label: "Not On Hold", value: "not on hold" },
                    ]}
                  />
                </Form.Item>
              </Col>
            </Row>
          </FormSection>

          <Row gutter={24}>
            <Col xs={24} lg={12}>
              <FormSection
                title="Communication"
                subtitle="Contact information"
                icon={<MailOutlined />}
              >
                <Form.Item
                  label="Communication Email"
                  name="email"
                  rules={[{ required: true }]}
                >
                  <Input />
                </Form.Item>
              </FormSection>
            </Col>

            <Col xs={24} lg={12}>
              <FormSection
                title="Transmission Settings"
                subtitle="Size limits for data transfer"
                icon={<SettingOutlined />}
              >
                <FormFieldGrid columns={2}>
                  <Form.Item label="Max Email Size (Bytes)" name="maxEmailSizeKb">
                    <Input />
                  </Form.Item>
                  <Form.Item label="Max Transmission Size (MB)" name="maxTransmissionSizeMb">
                    <Input />
                  </Form.Item>
                </FormFieldGrid>
              </FormSection>
            </Col>
          </Row>

          <FormSection
            title="EPS System Identifiers"
            subtitle="Electronic Position System credentials"
            icon={<SafetyOutlined />}
            variant="subtle"
          >
            <FormFieldGrid columns={2}>
              <Form.Item label="EPS System ID (Primary)" name="epsSystemId">
                <Input />
              </Form.Item>
              <Form.Item label="EPS System ID (Backup)" name="epsSystemIdBackup">
                <Input />
              </Form.Item>
            </FormFieldGrid>
          </FormSection>
        </Space>
      </Form>
    </Edit>
  );
};
